/** @format */

import React from "react";
import {
	IAccountPhoneNumbersCols,
	IAivrApps,
	IContext,
	IPhoneNumbers,
	ProvisioningStatus,
} from "./AccountPhoneNumbers.types";
import { Button, Popconfirm, Table, Tag, Tooltip } from "antd";
import Column from "antd/lib/table/Column";
import { capitalizeWord, formatDateTime } from "./utilities";

export interface IAccountPhoneNumbersProps {
	columns: IAccountPhoneNumbersCols;
	phoneNumberData?: IPhoneNumbers[];
	appData?: IAivrApps[];
	contexts?: IContext[];
	deletePhoneNumber?: (number: string) => Promise<void>;
	tableSize: "small" | "middle";
}

const AccountPhoneNumbers: React.FC<IAccountPhoneNumbersProps> = ({
	phoneNumberData,
	columns,
	appData,
	contexts,
	deletePhoneNumber,
	tableSize,
}) => {
	return (
		<Table
			dataSource={phoneNumberData}
			size={tableSize}
			scroll={{ x: "max-content" }}
			rowKey={"e164Number"}>
			{columns.number && (
				<Column title="Number" dataIndex="e164Number" key="number" />
			)}
			{columns.status && (
				<Column
					title="Status"
					dataIndex="provisioningStatus"
					key="provisioningStatus"
					align="center"
					filters={[
						{ text: "Provisioning", value: ProvisioningStatus.Provisioning },
						{ text: "Completed", value: ProvisioningStatus.Completed },
						{ text: "Error", value: ProvisioningStatus.Error },
						{ text: "Deleted", value: ProvisioningStatus.Deleted },
					]}
					onFilter={(
						value: string | number | boolean,
						phoneNumber: IPhoneNumbers
					) => {
						if (typeof value === "string" && phoneNumber) {
							return phoneNumber.provisioningStatus.indexOf(value) === 0;
						} else return false;
					}}
					render={(status) => {
						const renderTagColor = () => {
							if (status === ProvisioningStatus.Provisioning) return "orange";
							else if (status === ProvisioningStatus.Completed) return "green";
							else if (status === ProvisioningStatus.Error) return "red";
							else if (status === ProvisioningStatus.Deleted) return "gold";
						};

						return <Tag color={renderTagColor()}>{capitalizeWord(status)}</Tag>;
					}}
				/>
			)}
			{columns.tollFree && (
				<Column
					title="Toll Free"
					dataIndex="isTollFree"
					key="isTollFree"
					render={(tollFree) => {
						return (
							<Tag color={tollFree === true ? "green" : "red"}>
								{tollFree === true ? "Yes" : "No"}
							</Tag>
						);
					}}
				/>
			)}
			{columns.location && (
				<Column title="State" dataIndex="state" key="state" />
			)}
			{columns.setupCost && (
				<Column
					title="Setup Cost"
					dataIndex="setupCost"
					key="setupCost"
					align="center"
					render={(cost) => (cost ? "$" + cost.toFixed(2) : "$0")}
				/>
			)}
			{columns.monthlyCost && (
				<Column
					title="Monthly Cost"
					dataIndex="mrc"
					key="mrc"
					align="center"
					render={(cost) => (cost ? "$" + cost.toFixed(2) : "$0")}
				/>
			)}
			{columns.purchaseDate && (
				<Column
					title="Purchase Date"
					dataIndex="purchaseDate"
					key="purchaseDate"
					sorter={(
						a: { purchaseDate: string },
						b: { purchaseDate: string }
					) => {
						a.purchaseDate = a.purchaseDate || "";
						b.purchaseDate = b.purchaseDate || "";
						return a.purchaseDate.localeCompare(b.purchaseDate);
					}}
					render={(date) => (date ? formatDateTime(date) : null)}
				/>
			)}
			{columns.lastBilledDate && (
				<Column
					title="Last Billed"
					dataIndex="lastBilledDate"
					key="lastBilledDate"
					render={(date) => (date ? formatDateTime(date) : null)}
				/>
			)}
			<Column
				title="Last Monthly Charge"
				dataIndex="lastMRCDate"
				key="lastMRCDate"
				render={(date) => (date ? formatDateTime(date) : null)}
			/>
			{columns.context && appData && contexts && (
				<Column
					title="Context"
					dataIndex="e164Number"
					key="context"
					render={(number) => {
						return appData.map((app) => {
							if (app.mainDnis === number) {
								const foundContext = contexts.find(
									(context) => context.id === app.contextId
								);
								if (foundContext) return foundContext.name;
								else return null;
							} else return null;
						});
					}}
				/>
			)}
			{columns.apps && appData && (
				<Column
					title="Apps"
					dataIndex="e164Number"
					key="apps"
					render={(number) => {
						return appData.map((app) => {
							if (app.mainDnis === number)
								return (
									<Tag key={app.aivrAppId}>
										{app.name ? app.name : app.aivrAppId}
									</Tag>
								);
							else if (
								app.secondaryDnisList.find((secNumber) => secNumber === number)
							)
								return (
									<Tag key={app.aivrAppId}>
										{app.name ? app.name : app.aivrAppId}
									</Tag>
								);
							else return null;
						});
					}}
				/>
			)}
			{columns.delete && deletePhoneNumber && appData && (
				<Column
					title="Delete"
					dataIndex="e164Number"
					key="delete"
					align="center"
					render={(number) => {
						const hasAnApp = () => {
							let hasApp = false;
							appData.forEach((app) => {
								if (app.mainDnis === number) hasApp = true;
								else if (
									app.secondaryDnisList.find(
										(secNumber) => secNumber === number
									)
								)
									return (hasApp = true);
							});
							return hasApp;
						};
						return (
							<div>
								<Popconfirm
									title="Are you sure you want to delete this number?"
									onConfirm={() => deletePhoneNumber(number)}
									okText="Yes"
									cancelText="No"
									placement="topRight"
									disabled={hasAnApp()}>
									{hasAnApp() ? (
										<Tooltip
											title="Cannot delete phone numbers with apps."
											placement="rightTop">
											<Button
												color="danger"
												icon="delete"
												size="small"
												disabled={hasAnApp()}></Button>
										</Tooltip>
									) : (
										<Button
											color="danger"
											icon="delete"
											size="small"
											disabled={hasAnApp()}></Button>
									)}
								</Popconfirm>
							</div>
						);
					}}
				/>
			)}
		</Table>
	);
};

export default AccountPhoneNumbers;
